// Язык интерфейса: текущая локаль, подписи и перевод статической разметки.

import { MONTH_LABELS as LOCALIZED_MONTH_LABELS, VISUALIZATION_LABELS as LOCALIZED_VISUALIZATION_LABELS, collator, t } from '../i18n';
import { isAnonymous } from '../auth';
import type { Locale, VisualizationType, WidgetAppearance } from '../types';
import { api, withCachedCatalogs } from '../api';
import { setUiLocale, tr } from '../ui-i18n';

import { DEFAULT_APPEARANCE } from './model';
import { KPI_ICON_CHOICES } from './visual-catalog';
import { applyTheme, saveUserPreferences } from './theming';
import { controls, element } from './controls';
import { dashboardStatusLabel, setStatus, updateWidgetCounter } from './status';
import { escapeHtml, icon, refreshIcons } from './dom';
import { loadIndicatorSections, populateIndicators } from './indicators';
import { rerenderCachedWidgets } from './widgets';
import { refreshScopeFilterControls } from './scope-filter';
import { setBuilderOpen, setPaletteOpen, syncModeSegment } from './workspace';
import { state } from './state';
import { updateIndicatorControls, updateVisualizationOptions } from './widget-form';

export function currentLocale(): Locale {
  return state.preferences.locale;
}

export function currentCollator(): Intl.Collator {
  return collator(currentLocale());
}

export function localizedVisualizationLabel(visualization: VisualizationType): string {
  return LOCALIZED_VISUALIZATION_LABELS[currentLocale()][visualization];
}

export function localizedMonthLabel(month: number): string {
  return LOCALIZED_MONTH_LABELS[currentLocale()][month - 1] ?? String(month);
}

export function setLabelText(control: HTMLElement, text: string): void {
  const label = control.closest('label');
  const span = label?.querySelector(':scope > span');
  if (span) span.textContent = text;
}

export function setText(selector: string, text: string): void {
  const node = document.querySelector<HTMLElement>(selector);
  if (node) node.textContent = text;
}

export function setTitleAndAria(control: HTMLElement, text: string): void {
  control.setAttribute('title', text);
  control.setAttribute('aria-label', text);
}

export function setIconButton(control: HTMLElement, label: string, iconName: string, visibleText = false): void {
  control.setAttribute('aria-label', label);
  control.setAttribute('title', label);
  control.dataset.tooltip = label;
  control.innerHTML = `${icon(iconName)}${visibleText ? `<span>${escapeHtml(label)}</span>` : ''}`;
  refreshIcons(control);
}

export function setOptionText(select: HTMLSelectElement, value: string, text: string): void {
  const option = select.querySelector<HTMLOptionElement>(`option[value="${CSS.escape(value)}"]`);
  if (option) option.textContent = text;
}

export function setSelectPlaceholder(select: HTMLSelectElement, text: string): void {
  const option = select.querySelector<HTMLOptionElement>('option[value=""]');
  if (option) option.textContent = text;
}

export function updateDefaultDashboardTitle(locale: Locale): void {
  const defaultTitles = [t('ru', 'newDashboardTitle'), t('en', 'newDashboardTitle')];
  if (state.currentDashboardId == null && state.currentTemplateId == null && defaultTitles.includes(state.definition.title)) {
    state.definition.title = t(locale, 'newDashboardTitle');
    controls.dashboardName.value = state.definition.title;
    controls.canvasTitle.textContent = state.definition.title;
    // Заголовок в шапке помечен data-i18n-skip (название — данные
    // пользователя), поэтому название по умолчанию меняется здесь явно.
    controls.dashboardTitleText.textContent = state.definition.title;
    controls.dashboardTitleButton.setAttribute('title', state.definition.title);
  }
}

export function populatePaletteOptions(): void {
  const current = controls.palette.value || DEFAULT_APPEARANCE.palette;
  const labels: Record<WidgetAppearance['palette'], string> = currentLocale() === 'en'
    ? {
        dashboard: 'Dashboard colors',
        blue: 'Blue',
        green: 'Green',
        teal: 'Teal',
        orange: 'Orange',
        red: 'Red',
        purple: 'Purple',
        viridis: 'Viridis',
        cividis: 'Cividis · accessible',
        grayscale: 'Grayscale',
        diverging: 'Diverging',
      }
    : {
        dashboard: 'Цвета дашборда',
        blue: 'Синяя',
        green: 'Зелёная',
        teal: 'Бирюзовая',
        orange: 'Оранжевая',
        red: 'Красная',
        purple: 'Фиолетовая',
        viridis: 'Viridis',
        cividis: 'Cividis · доступная',
        grayscale: 'Монохромная',
        diverging: 'Расходящаяся',
      };
  controls.palette.innerHTML = Object.entries(labels)
    .map(([value, label]) => `<option value="${value}">${escapeHtml(label)}</option>`)
    .join('');
  controls.palette.value = Object.hasOwn(labels, current) ? current : DEFAULT_APPEARANCE.palette;
}

export function applyStaticLocale(): void {
  const locale = currentLocale();
  document.documentElement.lang = locale;
  document.title = t(locale, 'appTitle');
  updateDefaultDashboardTitle(locale);
  document.querySelector<HTMLElement>('.brand-title')!.textContent = t(locale, 'appTitle');
  setLabelText(controls.dashboardSelect, t(locale, 'dashboard'));
  controls.dashboardSelect.setAttribute('aria-label', t(locale, 'dashboardSelect'));
  setIconButton(controls.dashboardManagerButton, t(locale, 'openDashboards'), 'layout-dashboard');
  controls.dashboardTitleButton.dataset.tooltip = 'Переименовать дашборд';
  controls.dashboardTitleButton.setAttribute('aria-label', 'Переименовать дашборд');
  setIconButton(controls.openEmissManager, t(locale, 'emissDiagnostics'), 'sliders-horizontal', true);
  setIconButton(controls.sourcesMenuButton, t(locale, 'sources'), 'database');
  setIconButton(controls.sourcesEmissCatalog, t(locale, 'emissCatalog'), 'search', true);
  setIconButton(controls.sourcesEmissLog, t(locale, 'requestLog'), 'scroll-text', true);
  setIconButton(element<HTMLButtonElement>('#new-dashboard'), t(locale, 'newDashboard'), 'file-plus-2');
  setIconButton(element<HTMLButtonElement>('#save-dashboard'), t(locale, 'save'), 'save');
  setIconButton(controls.autoBuilderButton, 'Автоконструктор', 'sparkles');
  document.querySelector<HTMLElement>('.palette-heading h2')!.textContent = t(locale, 'data');
  controls.builderTitle.textContent = t(locale, 'properties');
  setIconButton(controls.submitWidget, state.editingWidgetId ? t(locale, 'updateSelectedData') : t(locale, 'addToDashboard'), state.editingWidgetId ? 'refresh-cw' : 'plus', true);
  setIconButton(controls.addAsNewWidget, t(locale, 'addAsNew'), 'copy-plus', true);
  setIconButton(element<HTMLButtonElement>('#reset-widget'), t(locale, 'resetAppearance'), 'rotate-ccw');
  setIconButton(controls.closeBuilder, t(locale, 'closePanel'), 'x');
  setIconButton(controls.togglePalette, t(locale, 'closePanel'), 'x');
  setIconButton(controls.showPalette, 'Виджет', 'square-plus');
  element<HTMLButtonElement>('#builder-backdrop').setAttribute('aria-label', t(locale, 'closePanel'));
  const dataSummaries = Array.from(document.querySelectorAll<HTMLElement>('#widget-form > details > summary'));
  const setSummary = (summaries: HTMLElement[], index: number, key: Parameters<typeof t>[1]) => {
    const summary = summaries[index];
    if (summary) summary.textContent = t(locale, key);
  };
  setSummary(dataSummaries, 0, 'data');
  setSummary(dataSummaries, 1, 'filters');
  setSummary(dataSummaries, 2, 'visualizationType');
  const appearanceSummaries = Array.from(document.querySelectorAll<HTMLElement>('#appearance-inspector > details > summary'));
  setSummary(appearanceSummaries, 0, 'styling');
  setSummary(appearanceSummaries, 1, 'advancedSettings');
  if (appearanceSummaries[2]) appearanceSummaries[2].textContent = locale === 'en' ? 'Widget settings' : 'Настройки виджета';
  setSummary(appearanceSummaries, 3, 'mapSettings');
  setSummary(appearanceSummaries, 4, 'conditionalFormatting');
  setLabelText(controls.source, t(locale, 'source'));
  setOptionText(controls.source, '', t(locale, 'allSources'));
  setOptionText(controls.source, 'worldbank', t(locale, 'worldbankSource'));
  setOptionText(controls.source, 'emiss', t(locale, 'emissSource'));
  setOptionText(controls.source, 'bdmo', t(locale, 'bdmoSource'));
  setOptionText(controls.source, 'settlements', t(locale, 'settlements'));
  setLabelText(controls.indicatorSection, t(locale, 'indicatorSection'));
  setSelectPlaceholder(controls.indicatorSection, t(locale, 'allSections'));
  setLabelText(controls.indicatorSearch, t(locale, 'indicatorSearch'));
  controls.indicatorSearch.placeholder = t(locale, 'indicatorSearchPlaceholder');
  controls.builderHelp.dataset.tooltip = t(locale, 'sourcesSeparatedText');
  controls.builderHelp.setAttribute('aria-label', t(locale, 'help'));
  setLabelText(controls.indicator, t(locale, 'indicator'));
  setLabelText(controls.territoryLevel, t(locale, 'territoryLevel'));
  setOptionText(controls.territoryLevel, 'federal_district', t(locale, 'federalDistricts'));
  setOptionText(controls.territoryLevel, 'region', t(locale, 'regions'));
  setOptionText(controls.territoryLevel, 'municipality', t(locale, 'municipalities'));
  setLabelText(controls.districtFilter, t(locale, 'federalDistrict'));
  setSelectPlaceholder(controls.districtFilter, t(locale, 'allDistricts'));
  setLabelText(controls.regionFilter, t(locale, 'region'));
  setSelectPlaceholder(controls.regionFilter, t(locale, 'allRegions'));
  setLabelText(controls.territorySearch, t(locale, 'territorySearch'));
  setLabelText(controls.territories, t(locale, 'territories'));
  const yearHeader = controls.years.closest('label')?.querySelector<HTMLElement>('.field-header-row > span:first-child');
  if (yearHeader) yearHeader.textContent = t(locale, 'years');
  setLabelText(controls.months, t(locale, 'months'));
  controls.months.closest('label')!.querySelector<HTMLElement>('.field-help')!.textContent = t(locale, 'monthsEmptyMeansAll');
  setLabelText(controls.visualization, t(locale, 'visualizationType'));
  setLabelText(controls.title, t(locale, 'widgetTitle'));
  controls.title.placeholder = t(locale, 'autoTitle');
  setLabelText(controls.palette, t(locale, 'palette'));
  setLabelText(controls.decimals, t(locale, 'decimals'));
  document.querySelector<HTMLLabelElement>('#legend-checkbox')!.nextElementSibling!.textContent = t(locale, 'showLegend');
  document.querySelector<HTMLLabelElement>('#labels-checkbox')!.nextElementSibling!.textContent = t(locale, 'showLabels');
  document.querySelector<HTMLLabelElement>('#reverse-checkbox')!.nextElementSibling!.textContent = t(locale, 'reversePalette');
  setLabelText(controls.orientation, t(locale, 'orientation'));
  setOptionText(controls.orientation, 'auto', t(locale, 'auto'));
  setOptionText(controls.orientation, 'horizontal', t(locale, 'horizontal'));
  setOptionText(controls.orientation, 'vertical', t(locale, 'vertical'));
  setLabelText(controls.sortOrder, t(locale, 'sorting'));
  setOptionText(controls.sortOrder, 'auto', t(locale, 'auto'));
  setOptionText(controls.sortOrder, 'desc', t(locale, 'sortDesc'));
  setOptionText(controls.sortOrder, 'asc', t(locale, 'sortAsc'));
  setOptionText(controls.sortOrder, 'none', t(locale, 'sourceOrder'));
  element<HTMLInputElement>('#top-n-input').closest('label')!.querySelector<HTMLElement>('.field-help')!.textContent = t(locale, 'topNHelp');
  setLabelText(controls.axisScale, t(locale, 'axisScale'));
  setOptionText(controls.axisScale, 'auto', t(locale, 'auto'));
  setOptionText(controls.axisScale, 'linear', t(locale, 'linear'));
  setOptionText(controls.axisScale, 'log', t(locale, 'logarithmic'));
  setLabelText(controls.xAxisMin, locale === 'en' ? 'X minimum' : 'Минимум X');
  setLabelText(controls.xAxisMax, locale === 'en' ? 'X maximum' : 'Максимум X');
  setLabelText(controls.axisMin, locale === 'en' ? 'Value axis minimum' : 'Минимум оси значений');
  setLabelText(controls.axisMax, locale === 'en' ? 'Value axis maximum' : 'Максимум оси значений');
  setLabelText(controls.includeZero, t(locale, 'zeroLine'));
  setOptionText(controls.includeZero, 'auto', t(locale, 'auto'));
  setOptionText(controls.includeZero, 'always', t(locale, 'alwaysIncludeZero'));
  setOptionText(controls.includeZero, 'never', t(locale, 'neverForceZero'));
  setLabelText(controls.dataZoom, t(locale, 'dataZoom'));
  setOptionText(controls.dataZoom, 'auto', t(locale, 'auto'));
  setOptionText(controls.dataZoom, 'always', t(locale, 'always'));
  setOptionText(controls.dataZoom, 'never', t(locale, 'disabled'));
  setLabelText(controls.showPoints, t(locale, 'pointMarkers'));
  setOptionText(controls.showPoints, 'auto', t(locale, 'auto'));
  setOptionText(controls.showPoints, 'always', t(locale, 'show'));
  setOptionText(controls.showPoints, 'never', t(locale, 'hide'));
  setLabelText(controls.legendPosition, t(locale, 'legendPosition'));
  setOptionText(controls.legendPosition, 'auto', t(locale, 'auto'));
  setOptionText(controls.legendPosition, 'top', t(locale, 'top'));
  setOptionText(controls.legendPosition, 'bottom', t(locale, 'bottom'));
  setOptionText(controls.legendPosition, 'right', t(locale, 'right'));
  document.querySelector<HTMLInputElement>('#smooth-checkbox')!.nextElementSibling!.textContent = t(locale, 'smoothLines');
  document.querySelector<HTMLInputElement>('#patterns-checkbox')!.nextElementSibling!.textContent = t(locale, 'duplicateColorsWithPatterns');
  setLabelText(controls.kpiAggregation, locale === 'en' ? 'KPI aggregation' : 'Агрегация KPI');
  setOptionText(controls.kpiAggregation, 'latest', locale === 'en' ? 'Latest value' : 'Последнее');
  setOptionText(controls.kpiAggregation, 'sum', locale === 'en' ? 'Sum' : 'Сумма');
  setOptionText(controls.kpiAggregation, 'mean', locale === 'en' ? 'Mean' : 'Среднее');
  setOptionText(controls.kpiAggregation, 'min', locale === 'en' ? 'Minimum' : 'Минимум');
  setOptionText(controls.kpiAggregation, 'max', locale === 'en' ? 'Maximum' : 'Максимум');
  setLabelText(controls.kpiComparison, locale === 'en' ? 'KPI comparison' : 'Сравнение KPI');
  setOptionText(controls.kpiComparison, 'previous', locale === 'en' ? 'Previous period' : 'Предыдущий период');
  setOptionText(controls.kpiComparison, 'first', locale === 'en' ? 'First period' : 'Первый период');
  setOptionText(controls.kpiComparison, 'target', locale === 'en' ? 'Target' : 'Цель');
  setOptionText(controls.kpiComparison, 'none', locale === 'en' ? 'None' : 'Нет');
  setLabelText(controls.kpiTarget, locale === 'en' ? 'KPI target' : 'Цель KPI');
  document.querySelector<HTMLInputElement>('#kpi-sparkline-checkbox')!.nextElementSibling!.textContent = locale === 'en' ? 'Mini chart' : 'Мини-график';
  setLabelText(controls.kpiSparklineStyle, locale === 'en' ? 'Mini chart style' : 'Стиль мини-графика');
  setLabelText(controls.kpiIconMode, locale === 'en' ? 'KPI icon' : 'Иконка KPI');
  setOptionText(controls.kpiIconMode, 'none', locale === 'en' ? 'No icon' : 'Без иконки');
  setOptionText(controls.kpiIconMode, 'auto', locale === 'en' ? 'Auto' : 'Авто');
  setOptionText(controls.kpiIconMode, 'custom', locale === 'en' ? 'Custom' : 'Своя');
  controls.kpiIconPickerField.querySelector<HTMLElement>(':scope > span')!.textContent = locale === 'en' ? 'Choose icon' : 'Выберите иконку';
  controls.kpiIconPicker.setAttribute('aria-label', locale === 'en' ? 'KPI icon' : 'Иконка KPI');
  KPI_ICON_CHOICES.forEach((choice) => {
    const label = locale === 'en' ? choice.labelEn : choice.label;
    const button = controls.kpiIconPicker.querySelector<HTMLButtonElement>(`[data-kpi-icon="${CSS.escape(choice.id)}"]`);
    if (!button) return;
    button.title = label;
    button.setAttribute('aria-label', label);
    const text = button.querySelector('span');
    if (text) text.textContent = label;
  });
  controls.kpiIconCustomField.querySelector<HTMLElement>(':scope > span')!.textContent = locale === 'en' ? 'Other Lucide icon name' : 'Другое имя Lucide';
  controls.kpiIcon.placeholder = locale === 'en' ? 'activity' : 'activity';
  setLabelText(controls.classification, t(locale, 'method'));
  setOptionText(controls.classification, 'quantile', t(locale, 'quantile'));
  setOptionText(controls.classification, 'equal', t(locale, 'equalIntervals'));
  setOptionText(controls.classification, 'jenks', t(locale, 'jenks'));
  setOptionText(controls.classification, 'manual', t(locale, 'manual'));
  setLabelText(controls.classCount, t(locale, 'classCount'));
  setLabelText(controls.manualBreaks, t(locale, 'classBreaks'));
  controls.manualBreaks.placeholder = t(locale, 'classBreaksPlaceholder');
  setLabelText(controls.mapScaleMode, t(locale, 'timeScaleComparable'));
  setOptionText(controls.mapScaleMode, 'global', t(locale, 'globalScale'));
  setOptionText(controls.mapScaleMode, 'current', t(locale, 'currentScale'));
  setOptionText(controls.mapScaleMode, 'fixed', t(locale, 'fixedScale'));
  controls.mapScaleMode.closest('label')!.querySelector<HTMLElement>('.field-help')!.textContent = t(locale, 'mapScaleHelp');
  setLabelText(controls.mapBasemap, t(locale, 'mapBasemap'));
  setOptionText(controls.mapBasemap, 'auto', t(locale, 'mapBasemapAuto'));
  setOptionText(controls.mapBasemap, 'blankLight', t(locale, 'mapBasemapBlankLight'));
  setOptionText(controls.mapBasemap, 'blankDark', t(locale, 'mapBasemapBlankDark'));
  setOptionText(controls.mapBasemap, 'osm', t(locale, 'mapBasemapOsm'));
  setOptionText(controls.mapBasemap, 'satellite', t(locale, 'mapBasemapSatellite'));
  setLabelText(controls.mapFixedMin, t(locale, 'scaleMinimum'));
  setLabelText(controls.mapFixedMax, t(locale, 'scaleMaximum'));
  setLabelText(controls.nullColor, t(locale, 'missingColor'));
  document.querySelector<HTMLInputElement>('#time-control-checkbox')!.nextElementSibling!.textContent = t(locale, 'showPeriodSelector');
  setLabelText(controls.conditionOperator, t(locale, 'condition'));
  setOptionText(controls.conditionOperator, 'none', t(locale, 'noCondition'));
  setOptionText(controls.conditionOperator, 'gt', t(locale, 'greaterThan'));
  setOptionText(controls.conditionOperator, 'gte', t(locale, 'greaterOrEqual'));
  setOptionText(controls.conditionOperator, 'lt', t(locale, 'lessThan'));
  setOptionText(controls.conditionOperator, 'lte', t(locale, 'lessOrEqual'));
  setLabelText(controls.conditionValue, t(locale, 'threshold'));
  setLabelText(controls.conditionColor, t(locale, 'color'));
  element<HTMLButtonElement>('#select-all-territories').textContent = t(locale, 'selectVisible');
  element<HTMLButtonElement>('#clear-territories').textContent = t(locale, 'clearSelection');
  controls.selectAllYears.textContent = t(locale, 'selectAll');
  controls.clearYears.textContent = t(locale, 'clearSelection');
  controls.inspectorEmpty.querySelector('strong')!.textContent = t(locale, 'selectWidget');
  controls.inspectorEmpty.querySelector('p')!.textContent = t(locale, 'selectWidgetHint');
  controls.layoutHelp.textContent = t(locale, 'layoutHelp');
  setIconButton(controls.toggleBuilder, state.builderOpen ? t(locale, 'hideInspector') : t(locale, 'showInspector'), 'panel-right');
  syncModeSegment();
  setIconButton(controls.toggleLayout, state.layoutEditing ? t(locale, 'finishLayout') : t(locale, 'editLayout'), state.layoutEditing ? 'check' : 'move');
  setIconButton(controls.toggleFilterbar, t(locale, 'filters'), 'filter');
  setIconButton(controls.compactLayout, 'Автоконфигурация', 'wand-sparkles');
  setIconButton(element<HTMLButtonElement>('#refresh-all'), t(locale, 'refreshData'), 'refresh-cw');
  if (!state.dirty) controls.dirtyIndicator.textContent = dashboardStatusLabel(false);
  element<HTMLButtonElement>('#reset-scope-filter').textContent = tr('По умолчанию');
  setText('#empty-state h3', t(locale, 'emptyDashboard'));
  setText('#empty-state p', t(locale, 'emptyDashboardHint'));
  controls.emptyAddWidget.querySelector('span')!.textContent = t(locale, 'addFirstWidget');
  controls.emptyAutoBuilder.querySelector('span')!.textContent = t(locale, 'runAutoBuilder');
  controls.emptyDashboardManager.querySelector('span')!.textContent = t(locale, 'openDashboards');
  setText('#dashboard-manager-title', t(locale, 'dashboardManagerTitle'));
  controls.dashboardManagerSearch.placeholder = t(locale, 'dashboardManagerSearchPlaceholder');
  controls.dashboardManagerTabs.setAttribute('aria-label', t(locale, 'dashboardSections'));
  controls.dashboardManagerDemoHint.textContent = t(locale, 'demoDashboardsHint');
  controls.dashboardManagerNew.querySelector('span')!.textContent = t(locale, 'newDashboard');
  setText('#auto-builder-title', t(locale, 'autoBuilderTitle'));
  controls.autoBuilderDialog.querySelector('.auto-builder-header p')!.textContent = t(locale, 'autoBuilderDescription');
  setLabelText(controls.autoSource, t(locale, 'source'));
  setOptionText(controls.autoSource, 'bdmo', t(locale, 'bdmoSource'));
  setOptionText(controls.autoSource, 'worldbank', t(locale, 'worldbankSource'));
  setOptionText(controls.autoSource, 'emiss', t(locale, 'emissSource'));
  setOptionText(controls.autoSource, 'settlements', t(locale, 'settlements'));
  setLabelText(controls.autoSection, t(locale, 'indicatorSection'));
  controls.autoSectionSearch.placeholder = t(locale, 'autoBuilderSectionSearch');
  setText('#auto-indicators-title', t(locale, 'autoBuilderIndicators'));
  setText('#auto-pick-enabled + span', t(locale, 'autoBuilderAutoPick'));
  setText('#auto-fill-missing + span', t(locale, 'autoBuilderAutoFill'));
  controls.autoIndicatorSearch.placeholder = t(locale, 'autoBuilderIndicatorSearch');
  setText('#auto-territory-title', t(locale, 'territories'));
  setText('#auto-bdmo-territory-title', t(locale, 'territories'));
  setText('#auto-bdmo-territory-panel .auto-cascade-column:nth-child(1) .auto-list-head > span:first-child', t(locale, 'autoBuilderDistricts'));
  setText('#auto-bdmo-territory-panel .auto-cascade-column:nth-child(2) .auto-list-head > span:first-child', t(locale, 'autoBuilderRegions'));
  setText('#auto-bdmo-territory-panel .auto-cascade-column:nth-child(3) .auto-list-head > span:first-child', t(locale, 'autoBuilderMunicipalities'));
  controls.autoTerritorySearch.placeholder = t(locale, 'autoBuilderTerritorySearch');
  controls.autoDistrictSearch.placeholder = t(locale, 'autoBuilderDistrictSearch');
  controls.autoRegionSearch.placeholder = t(locale, 'autoBuilderRegionSearch');
  controls.autoMunicipalitySearch.placeholder = t(locale, 'autoBuilderMunicipalitySearch');
  controls.autoDistrictSelectAll.textContent = t(locale, 'selectAll');
  controls.autoDistrictClear.textContent = t(locale, 'clearSelection');
  controls.autoRegionSelectAll.textContent = t(locale, 'selectAll');
  controls.autoRegionClear.textContent = t(locale, 'clearSelection');
  controls.autoMunicipalitySelectAll.textContent = t(locale, 'selectAll');
  controls.autoMunicipalityClear.textContent = t(locale, 'clearSelection');
  controls.autoTerritorySelectAll.textContent = t(locale, 'selectAll');
  controls.autoTerritoryClear.textContent = t(locale, 'clearSelection');
  setText('#auto-bdmo-territory-panel .field-help', t(locale, 'autoBuilderBdmoTerritoryHint'));
  setText('#auto-years-title', t(locale, 'years'));
  controls.autoYearSearch.placeholder = t(locale, 'autoBuilderYearSearch');
  controls.autoYearSelectAll.textContent = t(locale, 'selectAll');
  controls.autoYearClear.textContent = t(locale, 'clearSelection');
  setLabelText(controls.autoPrimaryColor, t(locale, 'primaryColor'));
  setText('#auto-color-title', t(locale, 'primaryColor'));
  setText('#auto-color-title + small', t(locale, 'autoBuilderColorHint'));
  controls.autoColorReset.textContent = t(locale, 'resetColor');
  controls.autoBuilderCancel.textContent = t(locale, 'cancel');
  controls.autoBuilderCreate.querySelector('span')!.textContent = t(locale, 'createDashboard');
  controls.globalStatus.textContent ||= t(locale, 'initialization');
  setText('#emiss-dialog .eyebrow', t(locale, 'remoteSource'));
  setText('#emiss-dialog-title', t(locale, 'emissManagerTitle'));
  setText('.emiss-dialog-header p', t(locale, 'emissManagerDescription'));
  controls.closeEmissManager.setAttribute('aria-label', t(locale, 'closeEmissManager'));
  if (!controls.emissConnectionBadge.textContent || controls.emissConnectionBadge.textContent.includes('Проверка') || controls.emissConnectionBadge.textContent.includes('Checking')) {
    controls.emissConnectionBadge.textContent = t(locale, 'checkingConfiguration');
  }
  setText('#emiss-catalog-title', t(locale, 'emissCatalog'));
  if (!state.emissCatalogStatus) controls.emissCatalogStatus.textContent = t(locale, 'catalogNotLoaded');
  controls.emissCatalogRefresh.textContent = t(locale, 'refreshCatalog');
  setLabelText(controls.emissCatalogQuery, t(locale, 'indicatorSearch'));
  controls.emissCatalogQuery.placeholder = t(locale, 'indicatorSearchPlaceholder');
  controls.emissCatalogSearch.textContent = t(locale, 'find');
  document.querySelector<HTMLInputElement>('#emiss-catalog-include-excluded')!.nextElementSibling!.textContent = t(locale, 'includeExcluded');
  setLabelText(controls.emissIndicatorId, t(locale, 'emissIndicatorId'));
  controls.emissIndicatorId.placeholder = t(locale, 'emissIndicatorPlaceholder');
  setLabelText(controls.emissLocalId, t(locale, 'localId'));
  controls.emissLocalId.placeholder = t(locale, 'localIdPlaceholder');
  setLabelText(controls.emissName, t(locale, 'catalogName'));
  controls.emissName.placeholder = t(locale, 'catalogNamePlaceholder');
  setLabelText(controls.emissUnit, t(locale, 'unit'));
  if (controls.emissUnit.value === t('ru', 'unitDefault') || controls.emissUnit.value === t('en', 'unitDefault')) controls.emissUnit.value = t(locale, 'unitDefault');
  document.querySelector<HTMLInputElement>('#emiss-force-refresh')!.nextElementSibling!.textContent = t(locale, 'forceRefreshMetadata');
  document.querySelector<HTMLInputElement>('#emiss-active')!.nextElementSibling!.textContent = t(locale, 'activateAfterCheck');
  document.querySelector<HTMLInputElement>('#emiss-overwrite')!.nextElementSibling!.textContent = t(locale, 'overwriteRegistration');
  controls.emissDiagnostics.textContent = t(locale, 'checkAccess');
  controls.emissInspect.textContent = t(locale, 'inspectIndicator');
  controls.emissRegister.textContent = t(locale, 'register');
  if (!state.emissInspection) controls.emissManagerMessage.textContent = t(locale, 'emissInitialMessage');
  setText('.emiss-secondary-heading h3', t(locale, 'latestRequests'));
  controls.refreshEmissLog.setAttribute('title', t(locale, 'refreshLog'));
  controls.refreshEmissLog.setAttribute('aria-label', t(locale, 'refreshLog'));
  if (!controls.emissRequestLog.querySelector('.request-log-item')) controls.emissRequestLog.innerHTML = `<p>${escapeHtml(t(locale, 'logNotLoaded'))}</p>`;
  document.querySelector<HTMLElement>('.emiss-security-note strong')!.textContent = t(locale, 'networkRouteTitle');
  document.querySelector<HTMLElement>('.emiss-security-note')!.childNodes[1]!.textContent = ` ${t(locale, 'networkRouteText')}`;
  element<HTMLElement>('#settings-title').textContent = t(locale, 'settings');
  setIconButton(controls.settingsFab, t(locale, 'settings'), 'settings');
  controls.settingsResetColors.textContent = t(locale, 'resetColors');
  const settingsRows = controls.settingsPopover.querySelectorAll<HTMLElement>('.settings-row > span:first-child');
  settingsRows[0]!.textContent = t(locale, 'language');
  settingsRows[1]!.textContent = t(locale, 'theme');
  controls.settingsThemeSystem.textContent = t(locale, 'system');
  controls.settingsThemeLight.textContent = t(locale, 'light');
  controls.settingsThemeDark.textContent = t(locale, 'dark');
  controls.settingsPrimaryColor.closest('label')!.querySelector('span')!.textContent = t(locale, 'primaryColor');
  controls.settingsColorWarning.textContent = t(locale, 'colorWarning');
  setPaletteOpen(state.paletteOpen, false);
  setBuilderOpen(state.builderOpen);
  populatePaletteOptions();
  updateVisualizationOptions(controls.visualization.value as VisualizationType);
  refreshScopeFilterControls();
  updateWidgetCounter();
}

// Смена языка ничего не запрашивает заново: данные и каталоги одинаковы на любом
// языке (переводит фронтенд, см. data-locale.ts). Каталоги переводятся из
// последних ответов (withCachedCatalogs), виджеты перерисовываются из кэша.
export async function reloadLocalizedCatalogsAndWidgets(): Promise<void> {
  const selectedIndicatorId = controls.indicator.value;
  if (isAnonymous()) {
    // Гостю каталоги закрыты: перерисовываются только данные общего дашборда.
    await rerenderCachedWidgets(true, true);
    return;
  }
  const [indicators, territories] = await withCachedCatalogs(() => Promise.all([
    api.indicators(true, currentLocale()),
    api.territories({}, currentLocale()),
  ]));
  state.indicators = indicators;
  state.localTerritories = territories;
    state.territories = [...territories];
    state.catalogTerritories = [...territories];
  await withCachedCatalogs(() => loadIndicatorSections());
  populateIndicators();
  if (state.indicators.some((indicator) => indicator.id === selectedIndicatorId)) {
    controls.indicator.value = selectedIndicatorId;
  }
  await withCachedCatalogs(() => updateIndicatorControls());
  await rerenderCachedWidgets(true, true);
}

export async function changeLocale(locale: Locale): Promise<void> {
  if (state.preferences.locale === locale) return;
  state.preferences.locale = locale;
  saveUserPreferences();
  setUiLocale(locale);
  applyTheme();
  applyStaticLocale();
  await reloadLocalizedCatalogsAndWidgets();
}

export function localizedUnitLabel(value: string | null | undefined): string {
  const source = (value ?? '').trim();
  if (!source) return '';
  const normalized = source.toLocaleLowerCase('ru-RU').replace(/\s+/g, ' ');
  if (currentLocale() === 'ru') {
    const ruMap: Record<string, string> = {
      people: 'человек',
      person: 'человек',
      persons: 'человек',
      percent: 'процент',
      'current us$': 'долл. США',
      'current lcu': 'нац. валюта',
      '%': '%',
    };
    return ruMap[normalized] ?? source;
  }
  const enMap: Record<string, string> = {
    человек: 'people',
    'чел.': 'people',
    процент: 'percent',
    процентов: 'percent',
    'тысяча рублей': 'thousand rubles',
    'тыс. рублей': 'thousand rubles',
  };
  return enMap[normalized] ?? source;
}
